import { useCallback, useState } from 'react';
import useApi from 'hooks/useApi';
import useFirebase from 'hooks/useFirebase';
import { CallStatus } from 'types/auth';

interface InitiateCallOptions {
  stepUid?: string;
  targetUserUids?: string[];
  sessionType?: string;
}

/**
 * Dashboard → app call initiation.
 *
 * 1. POST /events/call/initiate — the backend mints the Agora session (one token
 *    per participant) and fires the VoIP/FCM ring to the selected device(s).
 * 2. Write the call to Firebase via addCallData, which also writes the
 *    per-recipient `call-recipients/{uid}` pointers.
 * 3. Return the callId so the caller can navigate into the in-session call UI.
 */
const useInitiateCall = () => {
  const { initiateCallToUser, showToast } = useApi();
  const { addCallData } = useFirebase();
  const [isInitiating, setIsInitiating] = useState(false);

  const initiateCall = useCallback(
    async (transactionUid: string, opts?: InitiateCallOptions): Promise<string | undefined> => {
      if (!transactionUid) {
        showToast?.('No transaction available to start a call', 'error');
        return;
      }
      if (isInitiating) return;
      try {
        setIsInitiating(true);
        const res = await initiateCallToUser(transactionUid, opts);
        if (!res?.success || !res?.data) {
          showToast?.(res?.message || res?.data || 'Could not start the call', 'error');
          return;
        }

        const data = res.data;
        const callId: string = data.uid;
        const now = Date.now();

        const payload = {
          key: data.key,
          keyUid: data.keyUid,
          from: data.from,
          fromName: data.fromName,
          transaction: callId,
          to: [],
          status: CallStatus.CALLING,
          recipients: data.recipients || [],
          callDirection: 'INBOUND',
          sessionType: data.sessionType,
          session: data.session,
          initiated: now,
          userMuted: false,
          userHidden: false,
          supportMuted: false,
          isRecording: false,
          initGetLocation: false,
          screenshots: [],
          history: [{ status: CallStatus.CALLING, datetime: new Date(now).toISOString(), user: data.from }]
        };

        await addCallData(payload, callId);
        return callId;
      } catch (err: any) {
        showToast?.(err?.response?.data?.message || err?.message || 'Could not start the call', 'error');
      } finally {
        setIsInitiating(false);
      }
    },
    [initiateCallToUser, addCallData, isInitiating, showToast]
  );

  return { initiateCall, isInitiating };
};

export default useInitiateCall;
